import { useState } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import props from "../../redux/props";
import { getUUID } from "../../utility";
import Container from "../UI/Container/Container";
import ApplyForm from "./ApplyForm";
import opened from "../../assets/image/career/openPositions/opened.png";
import closed from "../../assets/image/career/openPositions/closed.png";

const blue = "rgb(0, 79, 139)";
const white = "rgb(255,255,255)";
const lightgray = "rgb(226,226,226)";

type Position = {
  title: string;
  tasks: string | string[];
  expectations: string | string[];
  offerings: string | string[];
};

const categories: Array<"tasks" | "expectations" | "offerings"> = [
  "tasks",
  "expectations",
  "offerings",
];

const StyledOpenPositions = styled.div`
  margin-top: 100px;
  font-family: "Open Sans", Helvetica, Arial, sans-serif;
`;

const Headline = styled.div`
  font-size: 32px;
  color: ${blue};
  text-align: center;
`;

const Selection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 25px;
`;

const Header = styled.div<{ active: boolean }>`
  position: relative;
  background-color: ${({ active }) => (active ? blue : lightgray)};
  color: ${({ active }) => (active ? white : blue)};
  font-size: 22px;
  text-transform: uppercase;
  padding: 10px 15px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 992px) {
    font-size: 18px;
    padding: 10px 50px 10px 5px;
  }
`;

const Plus = styled.div<{ active: boolean }>`
  position: absolute;
  width: 25px;
  height: 25px;
  background-image: url(${({ active }) => (active ? opened : closed)});
  background-position: center center;
  background-size: cover;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
`;

const Content = styled.div<{ active: boolean }>`
  padding: 15px;
  display: ${({ active }) => (active ? "flex" : "none")};
  flex-direction: column;
  gap: 25px;
  @media (max-width: 992px) {
    padding: 15px 5px;
  }
`;

const Split = styled.div`
  display: flex;
  gap: 25px;
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const Category = styled.div`
  width: 20%;
  color: ${blue};
  @media (max-width: 992px) {
    width: 100%;
  }
`;

const CategoryContent = styled.div`
  width: 80%;
  @media (max-width: 992px) {
    width: 100%;
  }
`;

const List = styled.ul`
  margin-left: 15px;
`;

const CareerOpenPositions = () => {
  const t = useSelector((s) => s[props.TRANSLATION]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const activate = (index: number) =>
    setActiveIndex(activeIndex === index ? null : index);

  return (
    <StyledOpenPositions id="open-positions">
      <Container>
        <Headline>{t.career.openPositions.headline}</Headline>
        <Selection>
          {t.career.openPositions.items.map((position: Position, i: number) => (
            <div key={getUUID()}>
              <Header active={activeIndex === i} onClick={() => activate(i)}>
                {position.title}
                <Plus active={activeIndex === i} />
              </Header>
              <Content active={activeIndex === i}>
                {categories.map((category) => (
                  <Split key={category}>
                    <Category>{t.career.openPositions[category]}</Category>
                    <CategoryContent>
                      {Array.isArray(position[category]) ? (
                        <List>
                          {(position[category] as string[]).map((entry) => (
                            <li key={getUUID()}>{entry}</li>
                          ))}
                        </List>
                      ) : (
                        position[category]
                      )}
                    </CategoryContent>
                  </Split>
                ))}
                <ApplyForm title={position.title} />
              </Content>
            </div>
          ))}
        </Selection>
      </Container>
    </StyledOpenPositions>
  );
};

export default CareerOpenPositions;
